/**
 * AttachDocsButton (Phase C, iter1086h)
 * -------------------------------------
 * Compact paperclip button that opens a dialog with the shared
 * SupportingDocsPanel for any entity in the platform. Use this to
 * expose upload / view / detach on:
 *
 *  - list rows (Fixed Assets, Loans, Investments, Prepayments,
 *    Deferred Revenue, Manual Journals, Recurring Journals, Inventory)
 *  - detail sheets (Bank Feed Uncategorised / Completed tabs)
 *  - GL breakdown row popover (via `variant="chip"`)
 *
 * The button auto-fetches the current attachment count so users see
 * whether a row already has docs BEFORE opening the dialog.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Paperclip, FileText, Image as ImageIcon, FileSpreadsheet, FileArchive } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import SupportingDocsPanel from './SupportingDocsPanel';
import { loadDocsFor } from './docsBatchLoader';

const TITLE_MAP = {
  invoice: 'Invoice documents',
  bill: 'Bill documents',
  bank_feed_txn: 'Bank transaction documents',
  manual_journal: 'Journal documents',
  recurring_journal: 'Recurring journal documents',
  canonical_txn: 'Transaction documents',
  prepayment: 'Prepayment documents',
  fixed_asset: 'Asset documents',
  loan: 'Loan documents',
  investment: 'Investment documents',
  deferred_revenue: 'Deferred revenue documents',
  other_receipt: 'Receipt documents',
  other_payment: 'Payment documents',
  inventory_item: 'Item documents',
  payment: 'Payment documents',
};

const AttachDocsButton = ({
  entityType,
  entityId,
  uploadedVia,
  label,             // optional label to show next to the icon
  variant = 'icon',  // 'icon' | 'chip' | 'row'
  className = '',
  disabled = false,
  refreshKey,        // bump to re-fetch (e.g. after external delete)
  hoverPreview = true, // Feb 2026: show doc thumbnails on hover before click
}) => {
  const [count, setCount] = useState(null);
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);

  const fetchCount = useCallback(async () => {
    if (!entityType || !entityId) { setCount(0); setDocs([]); return; }
    try {
      // Coalesced batch loader - N sibling AttachDocsButtons on the
      // same page fold into ONE POST /documents/for-batch call
      // (see docsBatchLoader.js). Prevents the 100+-connection storm
      // that used to exhaust Uvicorn's single worker.
      const r = await loadDocsFor(entityType, entityId);
      setCount(Number(r?.count) || 0);
      setDocs(Array.isArray(r?.documents) ? r.documents : []);
    } catch {
      setCount(0);
      setDocs([]);
    }
  }, [entityType, entityId]);

  useEffect(() => { fetchCount(); }, [fetchCount, refreshKey]);
  // Re-count when the panel dialog closes (in case user uploaded/detached).
  useEffect(() => { if (!open) fetchCount(); }, [open, fetchCount]);

  const hasDocs = (count || 0) > 0;

  // Feb 2026: tiny preview list rendered inside the HoverCard so users
  // can peek at attachments before opening the full dialog. Images use
  // an inline <img>; every other type falls back to a coloured icon.
  const _iconFor = (ct) => {
    const s = String(ct || '').toLowerCase();
    if (s.startsWith('image/')) return ImageIcon;
    if (s.includes('spreadsheet') || s.includes('excel') || s.endsWith('csv')) return FileSpreadsheet;
    if (s.includes('zip') || s.includes('archive')) return FileArchive;
    return FileText;
  };
  const previewCard = hasDocs && hoverPreview ? (
    <HoverCardContent
      side="top" align="center" sideOffset={6}
      className="w-72 p-2"
      data-testid={`attach-docs-preview-${entityType}-${entityId}`}
    >
      <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5 px-1">
        {count} document{count === 1 ? '' : 's'}
      </p>
      <ul className="space-y-1 max-h-52 overflow-y-auto">
        {docs.slice(0, 6).map((d) => {
          const isImg = String(d.content_type || '').toLowerCase().startsWith('image/');
          const Icon = _iconFor(d.content_type);
          return (
            <li key={d.id} className="flex items-center gap-2 rounded-md hover:bg-slate-50 p-1.5">
              {isImg ? (
                <img
                  src={d.url}
                  alt={d.original_name}
                  className="w-10 h-10 rounded object-cover border border-slate-200 shrink-0"
                  loading="lazy"
                />
              ) : (
                <span className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon className="w-4 h-4 text-slate-500" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-slate-800 truncate leading-tight">
                  {d.original_name}
                </p>
                <a
                  href={d.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] text-[#108981] hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Open in new tab
                </a>
              </div>
            </li>
          );
        })}
        {docs.length > 6 && (
          <li className="text-[10px] text-slate-500 italic text-center py-1">
            + {docs.length - 6} more · click to manage
          </li>
        )}
      </ul>
    </HoverCardContent>
  ) : null;

  const trigger = (() => {
    if (variant === 'chip') {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          disabled={disabled}
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold transition-colors border ${hasDocs ? 'bg-[#108981]/8 text-[#108981] border-[#108981]/30 hover:bg-[#108981]/12' : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'} disabled:opacity-50 ${className}`}
          data-testid={`attach-docs-chip-${entityType}-${entityId}`}
          aria-label={hasDocs ? `${count} documents attached` : 'Attach documents'}
        >
          <Paperclip className="w-3 h-3" />
          {hasDocs ? count : '+ Add'}
        </button>
      );
    }
    if (variant === 'row') {
      return (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          disabled={disabled}
          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors ${hasDocs ? 'text-[#108981] hover:bg-[#108981]/8' : 'text-slate-500 hover:bg-slate-100'} disabled:opacity-50 ${className}`}
          data-testid={`attach-docs-row-${entityType}-${entityId}`}
          title={hasDocs ? `${count} document${count === 1 ? '' : 's'} attached` : 'Attach documents'}
        >
          <Paperclip className="w-3.5 h-3.5" />
          {label || 'Docs'}
          {hasDocs && (
            <span className="rounded-full bg-[#108981]/12 text-[#108981] px-1.5 py-0.5 text-[10px]">{count}</span>
          )}
        </button>
      );
    }
    // default: 'icon' — small icon-only button with a count badge.
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        disabled={disabled}
        className={`relative inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors ${hasDocs ? 'text-[#108981] hover:bg-[#108981]/8' : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'} disabled:opacity-50 ${className}`}
        aria-label={hasDocs ? `${count} documents attached` : 'Attach documents'}
        data-testid={`attach-docs-icon-${entityType}-${entityId}`}
      >
        <Paperclip className="w-4 h-4" />
        {hasDocs && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#108981] text-white text-[9px] font-bold flex items-center justify-center leading-none">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>
    );
  })();

  return (
    <>
      {previewCard ? (
        <HoverCard openDelay={250} closeDelay={100}>
          <HoverCardTrigger asChild>
            <span className="inline-flex">{trigger}</span>
          </HoverCardTrigger>
          {previewCard}
        </HoverCard>
      ) : (
        trigger
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid={`attach-docs-dialog-${entityType}-${entityId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Paperclip className="w-4 h-4 text-[#108981]" />
              {TITLE_MAP[entityType] || 'Supporting documents'}
            </DialogTitle>
          </DialogHeader>
          <SupportingDocsPanel
            entityType={entityType}
            entityId={entityId}
            uploadedVia={uploadedVia || entityType}
            className="border-none p-0"
            disabled={disabled}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AttachDocsButton;
